import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import JsonLd from '../../components/base/JsonLd';

export default function ProjectsPage() {
  const [isNavFixed, setIsNavFixed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [messageLength, setMessageLength] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setFormStatus('submitting');
    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (!el.name) continue;
      if (el.tagName === 'SELECT') {
        formData.append(el.name, (el as HTMLSelectElement).options[(el as HTMLSelectElement).selectedIndex]?.text || el.value);
      } else {
        formData.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d7cq7qum3fsfopf23i20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setMessageLength(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://donghua-painting.com';

  const projectsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${siteUrl}/projects`,
    url: `${siteUrl}/projects`,
    name: '工程實績 | 台灣各地建築塗裝案例 | 東樺建築塗裝',
    description: '東樺建築塗裝500+完工案例，涵蓋公共工程、教育機構、科技廠房、住宅建案。遍佈全台的工程實績見證20年專業塗裝品質。',
    inLanguage: 'zh-TW',
    isPartOf: { '@id': `${siteUrl}/#website` },
    provider: { '@id': `${siteUrl}/#business` },
    about: {
      '@type': 'ItemList',
      name: '建築塗裝工程實績',
      description: '東樺建築塗裝遍佈全台的工程案例',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '中壢頤親園', description: '桃園市公共工程塗裝' },
        { '@type': 'ListItem', position: 2, name: '台中司法大廈', description: '台中市政府公共建築外牆塗裝' },
        { '@type': 'ListItem', position: 3, name: '桃園市政府警察局', description: '桃園市政府機關建築塗裝' },
        { '@type': 'ListItem', position: 4, name: '桃園國際機場總務處', description: '桃園國際機場廠房塗裝工程' },
        { '@type': 'ListItem', position: 5, name: '內湖區住宅', description: '台北市住宅建案外牆塗裝' },
        { '@type': 'ListItem', position: 6, name: '信義區住宅', description: '台北市高端住宅建案塗裝' },
      ],
    },
  };

  useEffect(() => {
    document.title = '工程實績 | 全台建築塗裝案例500+ | 東樺建築塗裝';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', '東樺建築塗裝500+完工案例，涵蓋公共工程、教育機構、科技廠房、住宅建案。遍佈全台的工程實績見證我們20年專業塗裝品質與用心。');
    }
    return () => {
      document.title = '東樺建築塗裝 | 台灣專業外牆塗裝 | 築於信任繪於精緻';
      if (metaDesc) {
        metaDesc.setAttribute('content', '東樺建築塗裝成立於2005年,擁有20年專業經驗的台灣專業建築外牆塗裝專家。採用德日進口塗料,提供高品質的建築外牆塗裝服務。');
      }
    };
  }, []);

  const categories = ['全部', '公共工程', '教育機構', '科技廠房', '室內', '住宅建案'];

  const projects = [
    {
      name: '中壢頤親園',
      category: '公共工程',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/74de3b02052837a5bf8174d286647159.jpeg',
      year: '2025',
      location: '桃園市',
      area: '22,000m²',
      duration: '6個月',
      description: '中壢頤親園塗裝工程展現了東樺對不同建築類型的專業服務能力。我們以尊重與莊重的態度，為園區建築提供優質的塗裝服務。色彩選擇上注重與自然環境的融合，採用沉穩典雅的色調，營造寧靜祥和的氛圍，讓家屬感受到溫暖與安慰。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/74de3b02052837a5bf8174d286647159.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/565d464d18299bbba81250da54c627f4.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d5b33f9fdde65ec330a1415376243906.jpeg',
      ],
    },
    {
      name: '桃園市政府警察局',
      category: '公共工程',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/2fa656cfff52bba158ea9ab023deb8f6.jpeg',
      year: '2020',
      location: '桃園市',
      area: '3,000m²',
      duration: '2個月',
      description: '桃園市政府警察局塗裝工程展現了東樺在政府機關建築的卓越服務能力。採用高耐候性塗料系統，確保建築外觀長期保持莊重專業的形象。施工期間嚴格配合警政單位作息，確保不影響日常治安維護工作，獲得業主高度肯定。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/7f8a8afcb916ad87336c1c4ce2055e81.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/a4ba26555791c36ba163b544bf91d442.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/2fa656cfff52bba158ea9ab023deb8f6.jpeg',
      ],
    },
    {
      name: '西螺分局',
      category: '公共工程',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/6ddb099c7941799f3385991d5f5f8af8.jpeg',
      year: '2023',
      location: '雲林縣',
      area: '3,000m²',
      duration: '2個月',
      description: '西螺分局建築塗裝工程為地方警政機關帶來煥然一新的面貌。我們採用耐久性優異的塗料系統，在有限的工期內完美完成施工任務。細膩的工藝與嚴謹的施工管理，讓建築外觀展現莊重威嚴的執法形象。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/6ddb099c7941799f3385991d5f5f8af8.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/358509f199711130d383f2993ba99006.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/8024c8b3ce138705e03b88ab43002ca1.jpeg',
      ],
    },
    {
      name: '台中司法大廈',
      category: '公共工程',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/ce514684f5f57c23d63e30c97ca18885.jpeg',
      year: '2017',
      location: '台中市',
      area: '20,000m²',
      duration: '6個月',
      description: '台中司法大廈外牆塗裝工程展現了東樺在公共建築領域的專業實力。採用高耐候型矽酸樹脂塗料，確保建築外觀在嚴苛氣候條件下依然保持亮麗。工程期間嚴格遵守施工規範，在不影響司法機關正常運作的前提下，完美完成塗裝任務。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/c995d73bc94c61e38fef176849904a05.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d626033fecbab26728212258f161adcd.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d626033fecbab26728212258f161adcd.jpeg',
      ],
    },
    {
      name: '台中地檢署',
      category: '公共工程',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/7b6d388d01076f2eddd1c1b97b103800.jpeg',
      year: '2018',
      location: '台中市',
      area: '3,000m²',
      duration: '3個月',
      description: '台中地檢署塗裝工程是東樺在司法機關建築的重要代表作。我們以嚴謹的施工態度，採用高品質防護塗料系統，確保建築外觀展現司法機關應有的莊嚴氣度。施工過程嚴格遵守政府採購規範，展現專業與誠信。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/7b6d388d01076f2eddd1c1b97b103800.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/928a13856fab84aa6805dc51bfb4075a.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/8879e544c63840f0f2ea4df604797b65.jpeg',
      ],
    },
    {
      name: '元長國小',
      category: '教育機構',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/940209d73f4614da39fc86e8272ed318.jpeg',
      year: '2020',
      location: '雲林縣',
      area: '2,000m²',
      duration: '1個月',
      description: '元長國小校園塗裝工程為偏鄉學校帶來煥然一新的面貌。我們使用環保水性塗料，搭配活潑明亮的色彩設計，為孩子們創造更好的學習環境。施工期間配合學校作息，將對教學活動的影響降到最低，獲得學校師生與家長的一致好評。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/940209d73f4614da39fc86e8272ed318.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/72e4a7b176cf10253beb45d3581600c4.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/b69c2274c59270e1e4f470a13f9252f6.jpeg',
      ],
    },
    {
      name: '梅山國小',
      category: '教育機構',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/59f39eb730f00accb5206240ca3036ed.jpeg',
      year: '2019',
      location: '嘉義縣',
      area: '1,000m²',
      duration: '1個月',
      description: '梅山國小校園塗裝工程坐落於嘉義山區，我們充分考量山區氣候特性，選用高耐候性環保塗料，確保在潮濕多雨的環境下仍能長期保持亮麗。色彩設計上融入自然山林元素，營造與周遭環境和諧共融的校園氛圍，讓孩子在美好的環境中快樂學習。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/59f39eb730f00accb5206240ca3036ed.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/588db8fea7d0021e6741afd6b8c35b02.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/59f39eb730f00accb5206240ca3036ed.jpeg',
      ],
    },
    {
      name: '中壢科技廠房',
      category: '科技廠房',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/cdc1b62677c1b7bcc70beedab2103ff8.jpeg',
      year: '2018',
      location: '桃園市',
      area: '15,000m²',
      duration: '5個月',
      description: '中壢科技廠房塗裝工程為大型工業廠區提供全面的外牆防護解決方案。採用工業級高耐候塗料系統，有效抵禦工業環境中的化學腐蝕與紫外線侵害。施工團隊以精準的工程管理，在不影響廠區正常運作的前提下，高效完成全棟塗裝任務。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/5991a2f9881571180be19047fc3fcfd6.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/a25c1057736ba89674b45135df921db8.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/cdc1b62677c1b7bcc70beedab2103ff8.jpeg',
      ],
    },
    {
      name: '桃園國際機場總務處',
      category: '科技廠房',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/296450e5c7a1847343f75608fe6f9ca8.png',
      year: '2021',
      location: '桃園市',
      area: '4,000m²',
      duration: '2個月',
      description: '桃園國際機場總務處塗裝工程要求極高的施工品質與時程控制。我們採用專業的工業級塗料系統，確保在高濕度環境下仍能提供優異的防護效果。施工團隊在嚴格的時程要求下，展現卓越的專業能力，準時完工並獲得業主高度肯定。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/78e8c37cfdd81965853ddf69cb8021b5.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/296450e5c7a1847343f75608fe6f9ca8.png',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/78e8c37cfdd81965853ddf69cb8021b5.jpeg',
      ],
    },
    {
      name: '八德陳公館',
      category: '室內',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/a13cc910bcbbdd945413eb24f871b32c.jpeg',
      year: '2017',
      location: '桃園市',
      area: '',
      duration: '',
      description: '八德陳公館室內塗裝工程展現了東樺在精緻住宅室內塗裝的專業能力。我們提供客製化色彩諮詢服務，協助屋主選擇最適合的塗料與色調，打造溫馨舒適的居家氛圍。施工過程細心保護每一件家具與裝潢，確保室內環境整潔如初。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/91552b14d64ad7c42822fe4d915e9bc9.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/60c5a241fedf79fecd5d518b69c01c5e.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/f14995528269bc6533e6e3ada1a25a9e.jpeg',
      ],
    },
    {
      name: '內湖區住宅',
      category: '住宅建案',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/30e5245b1d51d34cd4f05f72018cf6bd.jpeg',
      year: '2025',
      location: '台北市',
      area: '2,000m²',
      duration: '2個月',
      description: '內湖區住宅建案塗裝工程是東樺在台北高端住宅市場的代表作。我們採用頂級進口塗料，搭配精緻的施工工藝，為住戶打造永恆的建築之美。色彩選擇上注重與周邊環境的協調，展現優雅大器的建築風格，獲得建設公司與住戶的一致好評。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/f9e90ae4c969078e4ee65bede232977d.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/30e5245b1d51d34cd4f05f72018cf6bd.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/0694ae9f4a20494fc882df0ae6c1454d.jpeg',
      ],
    },
    {
      name: '信義區住宅',
      category: '住宅建案',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/babf46b1ca046c88515e92a33f835914.jpeg',
      year: '2025',
      location: '台北市',
      area: '2,000m²',
      duration: '2個月',
      description: '信義區住宅建案坐落於台北市精華地段，塗裝工程要求極高的品質標準。我們選用高端矽酸樹脂塗料，以細膩的工藝呈現建築的精�質感，與信義區的都會氛圍完美融合，彰顯居住者的品味與格調。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/babf46b1ca046c88515e92a33f835914.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/cebcae95e2fda5fae3393feb5d7626b8.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/ea530072ec7683741d3a795e1acaa1a6.jpeg',
      ],
    },
    {
      name: '桃園藝文特區',
      category: '住宅建案',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/51f109a1b747cd151c64c7a5f8a37d60.jpeg',
      year: '2017',
      location: '桃園市',
      area: '',
      duration: '1個月',
      description: '桃園藝文特區住宅建案塗裝工程展現了東樺在大型住宅開發案的豐富經驗。我們以系統化的施工管理，確保整體建案的塗裝品質一致性，並在規定工期內完美交付，獲得建設公司的高度肯定。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d9eea9ee8709b3916e888729bdc2bb3f.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/07ef7f4f7c4dc43b14687ad8abff8516.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/574c23517caafbd9e959ae6b5defd60c.jpeg',
      ],
    },
    {
      name: '雲林私人案',
      category: '住宅建案',
      image: 'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/67a1f9efb11121f2944a0298f000b17e.jpeg',
      year: '2013',
      location: '雲林縣',
      area: '1,000m²',
      duration: '1個月',
      description: '雲林私人住宅案展現了東樺在精�住宅塗裝的專業能力。我們提供客製化的色彩諮詢服務，協助屋主選擇最適合的塗料與色彩，融合雲林在地的自然風情，打造溫馨舒適的居家外觀。',
      gallery: [
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/67a1f9efb11121f2944a0298f000b17e.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/0c25d8280bf119ef77b14c8c3c5a3baf.jpeg',
        'https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/dd07c48898070a5db4f390c5cc052680.jpeg',
      ],
    },
  ];

  const filteredProjects = selectedCategory === '全部' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="projects-jsonld" data={projectsJsonLd} />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isNavFixed ? 'bg-white shadow-lg' : 'bg-white shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d57786512cad84463bdc66c219dc39c4.png"
              alt="東樺建築塗裝"
              className="h-12"
            />
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-900 hover:text-[#D4AF37] transition-colors cursor-pointer"
            >
              首頁
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-900 hover:text-[#D4AF37] transition-colors cursor-pointer"
            >
              關於東樺
            </Link>
            <Link
              to="/technology"
              className="text-sm font-medium text-gray-900 hover:text-[#D4AF37] transition-colors cursor-pointer"
            >
              專業工藝
            </Link>
            <a
              href="/#contact"
              className="bg-[#D4AF37] text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-[#B8941F] transition-colors whitespace-nowrap cursor-pointer"
            >
              聯絡我們
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#34495E] to-[#2C3E50]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              工程實績
            </div>
            <h1 className="text-6xl font-bold mb-6">
              一座座建築
              <br />
              <span className="text-[#D4AF37]">是我們榮耀的印記</span>
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
              從公共建築到教育機構，從科技廠房到住宅建案，遍佈全台的工程實績見證我們的專業與用心
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-white sticky top-[72px] z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-[#D4AF37] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          <div className="text-center mt-6 text-sm text-gray-500">
            共 {filteredProjects.length} 個案例
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-gradient-to-br from-[#F8F9FA] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={index}
                onClick={() => setSelectedProject(index)}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-image-line text-[#D4AF37] text-lg"></i>
                  </div>
                </div>
                <div className="p-6">
                  <div className="inline-block px-3 py-1 bg-[#D4AF37]/10 rounded-full text-xs font-medium text-[#D4AF37] mb-3">
                    {project.category}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {project.name}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-[#D4AF37]"></i>
                      {project.location}
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-line text-[#D4AF37]"></i>
                      完工年份: {project.year}
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-building-line text-[#D4AF37]"></i>
                      施工面積: {project.area}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 overflow-y-auto"
          onClick={() => setSelectedProject(null)}
        >
          <div className="min-h-screen py-12 px-4">
            <button
              onClick={() => setSelectedProject(null)}
              className="fixed top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all cursor-pointer z-10"
            >
              <i className="ri-close-line text-white text-2xl"></i>
            </button>

            <div
              className="max-w-6xl mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Project Header */}
              <div className="text-center mb-12">
                <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 backdrop-blur-sm rounded-full text-[#D4AF37] text-sm font-medium mb-4">
                  {filteredProjects[selectedProject].category}
                </div>
                <h2 className="text-5xl font-bold text-white mb-6">
                  {filteredProjects[selectedProject].name}
                </h2>
                <div className="flex flex-wrap justify-center gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-[#D4AF37]"></i>
                    {filteredProjects[selectedProject].location}
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-line text-[#D4AF37]"></i>
                    完工年份: {filteredProjects[selectedProject].year}
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-building-line text-[#D4AF37]"></i>
                    {filteredProjects[selectedProject].area}
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-[#D4AF37]"></i>
                    工期: {filteredProjects[selectedProject].duration}
                  </div>
                </div>
              </div>

              {/* Project Description */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <i className="ri-file-text-line text-[#D4AF37]"></i>
                  專案說明
                </h3>
                <p className="text-white/80 leading-relaxed text-lg">
                  {filteredProjects[selectedProject].description}
                </p>
              </div>

              {/* Project Gallery */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-gallery-line text-[#D4AF37]"></i>
                  施工照片
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {filteredProjects[selectedProject].gallery.map((image, imgIndex) => (
                    <div
                      key={imgIndex}
                      className="aspect-[4/3] overflow-hidden rounded-xl bg-black/30"
                    >
                      <img
                        src={image}
                        alt={`施工照片 ${imgIndex + 1}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center">
                <p className="text-white/60 text-sm mb-4">點擊任意處關閉</p>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="bg-[#D4AF37] text-white px-8 py-3 rounded-full font-medium hover:bg-[#B8941F] transition-all cursor-pointer whitespace-nowrap"
                >
                  關閉詳情
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full text-[#D4AF37] text-sm font-medium mb-6">
              工程實錄
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">施工現場紀錄</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              實地記錄東樺團隊的專業施工過程，見證每一座建築的蛻變
            </p>
          </div>
          <div className="aspect-video bg-[#34495E]/10 rounded-2xl flex items-center justify-center border-2 border-[#D4AF37]/20 overflow-hidden relative group cursor-pointer" onClick={() => setShowVideoModal(true)}>
            <img
              src="https://public.readdy.ai/ai/img_res/813f1e751ced9117a535f2a12ee52fd9.jpg"
              alt="工程實錄影片"
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mb-4 shadow-2xl group-hover:scale-110 transition-transform">
                <i className="ri-play-fill text-4xl text-white ml-1"></i>
              </div>
              <p className="text-white font-medium text-lg">觀看工程實錄影片</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowVideoModal(false)}
        >
          <button
            onClick={() => setShowVideoModal(false)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-all cursor-pointer z-10"
          >
            <i className="ri-close-line text-white text-2xl"></i>
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-8">
              <h3 className="text-4xl font-bold text-white mb-4">東樺建築塗裝 · 工程實錄</h3>
              <p className="text-white/60 text-lg">遍佈全台的工程實績，見證我們的專業與用心</p>
            </div>
            <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/nKJwzpE8o6w"
                title="東樺工程實錄影片"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="text-center mt-8">
              <p className="text-white/60 text-sm">點擊任意處關閉</p>
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left Info */}
            <div>
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/10 rounded-full text-[#D4AF37] text-sm font-medium mb-6">
                聯絡我們
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">
                開始您的
                <br />
                建築美學之旅
              </h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                無論是新建工程或是老屋翻新，我們都能為您提供最專業的塗裝解決方案。立即聯繫我們，讓專業團隊為您服務。
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">聯絡電話</div>
                    <div className="text-lg font-medium text-gray-900">03-3900-250</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">電子郵件</div>
                    <div className="text-lg font-medium text-gray-900">tunghua69566488@gmail.com</div>
                    <div className="text-lg font-medium text-gray-900">sales@tung-hua.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-[#D4AF37]"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">公司地址</div>
                    <div className="text-lg font-medium text-gray-900">台灣 334桃園市八德區中正二街2號</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="bg-gradient-to-br from-[#F8F9FA] to-[#E8E9EB] rounded-2xl p-8">
              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-check-line text-4xl text-[#D4AF37]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">諮詢已送出！</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    感謝您的聯繫，我們的專業團隊將在 1-2 個工作天內與您聯絡。
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="bg-[#D4AF37] text-white px-8 py-3 rounded-full font-medium hover:bg-[#B8941F] transition-all cursor-pointer whitespace-nowrap"
                  >
                    再次諮詢
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  data-readdy-form
                  onSubmit={handleFormSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors text-sm"
                        placeholder="請輸入姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        聯絡電話 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors text-sm"
                        placeholder="請輸入電話"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電子郵件
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors text-sm"
                      placeholder="請輸入電子郵件"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      服務類型 <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="service_type"
                        required
                        className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors text-sm appearance-none cursor-pointer"
                      >
                        <option value="">請選擇服務類型</option>
                        <option value="exterior_painting">外牆塗裝</option>
                        <option value="waterproofing">防水工程</option>
                        <option value="renovation">老屋翻新</option>
                        <option value="interior_painting">室內塗裝</option>
                        <option value="public_works">公共工程</option>
                        <option value="other">其他服務</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-[#D4AF37]"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      工程地址
                    </label>
                    <input
                      type="text"
                      name="project_address"
                      className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors text-sm"
                      placeholder="請輸入工程地址（縣市、區域即可）"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      預算範圍
                    </label>
                    <div className="relative">
                      <select
                        name="budget_range"
                        className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors text-sm appearance-none cursor-pointer"
                      >
                        <option value="">請選擇預算範圍</option>
                        <option value="under_100k">10萬以下</option>
                        <option value="100k_300k">10萬 - 30萬</option>
                        <option value="300k_500k">30萬 - 50萬</option>
                        <option value="500k_1m">50萬 - 100萬</option>
                        <option value="over_1m">100萬以上</option>
                        <option value="unknown">尚未確定</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-[#D4AF37]"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      諮詢內容
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      onChange={(e) => setMessageLength(e.target.value.length)}
                      className="w-full px-4 py-3 border-b-2 border-[#D4AF37] bg-transparent focus:outline-none focus:border-[#B8941F] transition-colors resize-none text-sm"
                      placeholder="請描述您的需求，例如建築類型、面積、特殊要求等..."
                    ></textarea>
                    <div className="text-right text-xs text-gray-400 mt-1">{messageLength}/500</div>
                  </div>

                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 px-4 py-3 rounded-lg">
                      <i className="ri-error-warning-line"></i>
                      送出失敗，請稍後再試或直接來電聯繫我們。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || messageLength > 500}
                    className="w-full bg-[#D4AF37] text-white py-4 rounded-full font-medium hover:bg-[#B8941F] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        送出中...
                      </>
                    ) : '送出諮詢'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white">
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid md:grid-cols-4 gap-12">
              <div>
                <img
                  src="https://static.readdy.ai/image/67a293e5c2736117b5bb4e6bdb5ab7d5/d57786512cad84463bdc66c219dc39c4.png"
                  alt="東樺建築塗裝"
                  className="h-12 mb-4"
                />
                <p className="text-white/60 text-sm leading-relaxed">
                  三十年專注建築外牆塗裝，以專業工藝守護每一座建築的生命。
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-4">快速連結</h4>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      首頁
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      關於東樺
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/technology"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      專業工藝
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/projects"
                      className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm cursor-pointer"
                    >
                      工程實績
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-4">聯絡資訊</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-phone-line"></i>
                    03-3900-250
                  </li>
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-mail-line"></i>
                    tunghua69566488@gmail.com
                  </li>
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-mail-line"></i>
                    sales@tung-hua.com
                  </li>
                  <li className="flex items-center gap-2 text-white/60">
                    <i className="ri-map-pin-line"></i>
                    台灣 334桃園市八德區中正二街2號
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-bold mb-4">追蹤我們</h4>
                <div className="flex gap-3">
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all cursor-pointer"
                  >
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all cursor-pointer"
                  >
                    <i className="ri-line-fill"></i>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] transition-all cursor-pointer"
                  >
                    <i className="ri-instagram-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-white/40">
              © 2024 東樺建築塗裝有限公司. All rights reserved.
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/40 hover:text-[#D4AF37] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
